import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { cn } from '@/lib/utils';
import { createRole, updateRole } from './teamApi';

export const SCOPE_LABELS = {
  company: 'Whole company',
  team: "Their team's jobs",
  assigned_jobs: 'Assigned jobs',
  own_jobs: 'Their own jobs',
  assigned_interviews: 'Assigned interviews',
};

const SCOPE_HINTS = {
  company: 'Every job and candidate.',
  team: "Jobs they're on, and the jobs of the team they manage.",
  assigned_jobs: "Only the jobs they're put on.",
  own_jobs: "Only jobs where they're the hiring manager.",
  assigned_interviews: 'Only the candidates they interview.',
};

const NAME_LIMIT = 60;
const STARTER = ['jobs.view', 'candidates.view'];

const selectClass =
  'h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60';

const errorMessage = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

/** Everything `keys` brings with it: each permission's requirements, and theirs. */
const withRequirements = (keys, requires) => {
  const result = new Set(keys);
  const queue = [...result];
  while (queue.length) {
    for (const needed of requires.get(queue.pop()) || []) {
      if (!result.has(needed)) {
        result.add(needed);
        queue.push(needed);
      }
    }
  }
  return result;
};

/** Everything that would lose a requirement if `key` went, and so goes too. */
const withDependents = (key, held, requires) => {
  const gone = new Set([key]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const candidate of held) {
      if (!gone.has(candidate) && (requires.get(candidate) || []).some((needed) => gone.has(needed))) {
        gone.add(candidate);
        grew = true;
      }
    }
  }
  return gone;
};

/**
 * Builds or edits a custom role from the permission catalog. What it offers matches
 * what the server accepts: ticking a permission ticks what it needs, a limited scope
 * can't take what only works for the whole company, and nobody can give a permission
 * their own role doesn't have. The server checks all of it again.
 *
 * role: the custom role being edited, or null to make a new one, starting from
 * `start` ({ name, scope, permissions }) when duplicating.
 */
export default function RoleEditor({ role, start, permissions, freePermissions, grantable, onSaved, onCancel }) {
  const requires = useMemo(() => new Map(permissions.map((p) => [p.key, p.requires || []])), [permissions]);
  const companyOnly = useMemo(
    () => new Set(permissions.filter((p) => p.whole_company_only).map((p) => p.key)),
    [permissions]
  );
  const mayGive = useMemo(() => new Set(grantable || permissions.map((p) => p.key)), [grantable, permissions]);

  const source = role || start;
  const [name, setName] = useState(role ? role.name : start ? `Copy of ${start.name}`.slice(0, NAME_LIMIT) : '');
  const [scope, setScope] = useState(source?.scope || 'company');
  // A duplicate starts from what the new role can actually hold, and says what it left out.
  const [held, setHeld] = useState(() => {
    const keys = source ? source.permissions : STARTER;
    return new Set(keys.filter((key) => mayGive.has(key)
      && !((source?.scope || 'company') !== 'company' && companyOnly.has(key))));
  });
  const [note, setNote] = useState(() => {
    const left = start ? start.permissions.filter((key) => !held.has(key)) : [];
    if (!left.length) return '';
    const one = left.length === 1;
    const what = `${left.length} of ${start.name}'s permissions ${one ? 'was' : 'were'} left out`;
    const notYours = left.filter((key) => !mayGive.has(key)).length;
    if (!notYours) return `${what}: ${one ? 'it only works' : 'they only work'} for the whole company.`;
    if (notYours === left.length) return `${what}: your role doesn't have ${one ? 'it' : 'them'}.`;
    return `${what}: your role doesn't have some, and the rest only work for the whole company.`;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const limited = scope !== 'company';

  /** Why a permission can't be ticked, or '' when it can. A requirement's reason counts too. */
  const blockedReason = (key) => {
    const chain = withRequirements([key], requires);
    if ([...chain].some((k) => limited && companyOnly.has(k))) return 'Whole company only';
    if ([...chain].some((k) => !mayGive.has(k))) return 'Not in your role';
    return '';
  };

  const areas = useMemo(() => {
    const byArea = new Map();
    for (const permission of permissions) {
      if (!byArea.has(permission.area)) byArea.set(permission.area, []);
      byArea.get(permission.area).push(permission);
    }
    return [...byArea.entries()];
  }, [permissions]);

  const usesSeat = freePermissions ? [...held].some((key) => !freePermissions.includes(key)) : null;

  const toggle = (key) => {
    setNote('');
    setHeld((current) => {
      if (current.has(key)) {
        const gone = withDependents(key, current, requires);
        return new Set([...current].filter((k) => !gone.has(k)));
      }
      return new Set([...current, ...withRequirements([key], requires)]);
    });
  };

  const changeScope = (next) => {
    setScope(next);
    if (next === 'company') return;
    const removed = [...held].filter((key) => companyOnly.has(key));
    if (removed.length) {
      const keep = [...held].filter((key) => !companyOnly.has(key));
      setHeld(new Set(keep));
      setNote(removed.length === 1
        ? '1 permission that only works for the whole company was removed.'
        : `${removed.length} permissions that only work for the whole company were removed.`);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = name.trim().replace(/\s+/g, ' ');
    if (!cleaned) {
      setError('Name the role.');
      return;
    }
    if (!held.size) {
      setError('Choose at least one thing this role can do.');
      return;
    }
    setSaving(true);
    setError('');
    const body = {
      name: cleaned,
      scope,
      permissions: permissions.map((p) => p.key).filter((key) => held.has(key)),
    };
    try {
      if (role) {
        await updateRole(role.key, { ...body, version: role.version });
        toast.success(`${cleaned} was saved`);
      } else {
        await createRole(body);
        toast.success(`${cleaned} was created`);
      }
      await onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the role.'));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white" noValidate aria-label={role ? `Edit ${role.name}` : 'New role'}>
      <div className="grid gap-3 border-b border-slate-100 p-3 sm:grid-cols-[1fr_16rem]">
        <div>
          <Label htmlFor="role-name" className="text-xs">Role name</Label>
          <Input id="role-name" className="h-9" value={name} maxLength={NAME_LIMIT} autoFocus disabled={saving}
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="role-scope" className="text-xs">Can see</Label>
          <select id="role-scope" className={selectClass} value={scope} disabled={saving}
            onChange={(e) => changeScope(e.target.value)}>
            {Object.entries(SCOPE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <p className="mt-1 text-[11px] text-muted-foreground">{SCOPE_HINTS[scope]}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 px-3 pt-2.5 text-xs text-muted-foreground">
        <span>Ticking something also ticks what it needs, like seeing offers before sending them.</span>
        {usesSeat === null ? null : (
          <span className={cn('font-medium', usesSeat ? 'text-amber-700' : 'text-emerald-700')}>
            {usesSeat ? 'Uses a seat on your plan' : "Doesn't use a seat"}
          </span>
        )}
      </div>
      {note ? <p className="px-3 pt-1.5 text-xs text-amber-700" role="status">{note}</p> : null}

      <div className="grid gap-x-6 gap-y-3 p-3 sm:grid-cols-2">
        {areas.map(([area, entries]) => (
          <fieldset key={area}>
            <legend className="mb-1 flex w-full items-baseline justify-between text-xs font-semibold text-slate-700">
              <span>{area}</span>
              <span className="font-normal tabular-nums text-muted-foreground">
                {entries.filter((p) => held.has(p.key)).length}/{entries.length}
              </span>
            </legend>
            <ul className="space-y-0.5">
              {entries.map((permission) => {
                const reason = held.has(permission.key) ? '' : blockedReason(permission.key);
                return (
                  <li key={permission.key}>
                    <label className={cn('flex items-start gap-2 rounded px-1 py-0.5 text-xs',
                      reason ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-700 hover:bg-slate-50')}>
                      <input
                        type="checkbox"
                        className="mt-0.5 h-3.5 w-3.5 accent-primary"
                        checked={held.has(permission.key)}
                        disabled={saving || Boolean(reason)}
                        onChange={() => toggle(permission.key)}
                      />
                      <span className="min-w-0 flex-1">{permission.label}</span>
                      {reason ? <span className="shrink-0 text-[10px] uppercase tracking-wide">{reason}</span> : null}
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        ))}
      </div>

      {error ? (
        <div role="alert" className="mx-3 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      <div className="flex justify-end gap-2 border-t border-slate-100 p-3">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {role ? 'Save role' : 'Create role'}
        </Button>
      </div>
    </form>
  );
}
